import { Box, Flex, Text, Grid } from '@chakra-ui/core'
import { css } from '@emotion/core'
import { StatusBlock } from 'components/ui/StatusBlock'
import useLocaleText from 'components/useLocaleText'
import keys from 'constants/keys'
import { AlarmClockCheck, ChartSpline, CircleCheckBig, ThumbsUp, ThumbsDown, Hash } from 'lucide-react'
import React, { memo, useEffect, useState, useMemo } from 'react'
import { DeepDiveInsights } from 'redux/actions/tasks_v2'
import { CHART_COLORS } from 'utils/taskAnalytics'

export const calculateTaskStats = (tasks: Record<string, { status: number; taskContextId: string }>): TaskStats => {
  const stats: TaskStats = {
    totalTasks: 0,
    statuses: {
      todo: 0,
      inProgress: 0,
      done: 0,
      approved: 0,
      rejected: 0,
    },
  }

  Object.values(tasks).forEach((task) => {
    stats.totalTasks += 1
    if (task.status === keys.TASK_STATUSES.TODO.value) {
      stats.statuses.todo += 1
    } else if (task.status === keys.TASK_STATUSES.INPROGRESS.value) {
      stats.statuses.inProgress += 1
    } else if (
      task.status === keys.TASK_STATUSES.DONE.value ||
      task.status === keys.TASK_STATUSES.SKIPPED.value ||
      task.status === -1
    ) {
      stats.statuses.done += 1
    } else if (task.status === keys.TASK_STATUSES.APPROVED.value) {
      stats.statuses.approved = (stats.statuses.approved || 0) + 1
    } else if (task.status === keys.TASK_STATUSES.DISAPPROVED.value) {
      stats.statuses.rejected = (stats.statuses.rejected || 0) + 1
    }
  })

  return stats
}

interface TaskStatusItemProps {
  title: string
  count: number
  bgColor: string
  iconBgColor: string
  children?: React.ReactNode
  isEmpty?: boolean
  symbol?: string
  isShowIcons: boolean
  isLoaded?: boolean
  icon: React.ReactNode
}
const TaskStatusItem: React.FC<TaskStatusItemProps> = ({
  title,
  count,
  bgColor,
  iconBgColor,
  children,
  isEmpty,
  symbol,
  isLoaded,
  isShowIcons,
  icon,
}) => {
  const [animatedCount, setAnimatedCount] = useState(0)

  useEffect(() => {
    if (!isEmpty) {
      let currentCount = 0
      const step = Math.ceil(count / 50)
      const interval = setInterval(() => {
        currentCount += step
        if (currentCount >= count) {
          setAnimatedCount(count)
          clearInterval(interval)
        } else {
          setAnimatedCount(currentCount)
        }
      }, 20)

      return () => clearInterval(interval)
    }
  }, [count, isEmpty])

  const isWhiteBackground = bgColor === '#FFFFFF'

  return (
    <StatusBlock
      background={bgColor}
      p={isEmpty ? '10px' : isLoaded ? '15px' : '10px'}
      style={{
        transform: isLoaded ? 'scaleY(1)' : 'scaleY(0.6)',
        height: isLoaded ? 'auto' : '20px',
        transition: 'all 0.5s ease-in-out',
        overflow: 'hidden',
        border: isWhiteBackground ? '1px solid #E2E8F0' : 'none',
        boxShadow: isWhiteBackground ? '0px 1px 3px rgba(0, 0, 0, 0.1)' : 'none',
      }}
    >
      {!isEmpty && (
        <>
          <Text fontSize="12px" fontWeight="bold" color={iconBgColor} textTransform="uppercase" mb="8px">
            {title}
          </Text>
          <Flex alignItems="center" w="100%" justifyContent="space-between">
            <Text fontSize="28px" fontWeight="bold" color={iconBgColor}>
              {animatedCount}
              {symbol}
            </Text>
            {children}
            {isShowIcons && (
              <Box borderRadius="full" background={iconBgColor} p="12px">
                {icon}
              </Box>
            )}
          </Flex>
        </>
      )}
    </StatusBlock>
  )
}

type TaskStats = {
  totalTasks: number
  statuses: { todo: number; inProgress: number; done: number; approved?: number; rejected?: number } // NEW: Optional approved and rejected
}
interface TaskStatusesBlocksProps {
  isEmpty?: boolean
  deepDiveInsights?: DeepDiveInsights
  isShowIcons?: boolean
  symbol?: string
  analytics?: Array<{ name: string; value: number }>
  totalTasksCount?: number
  hasVMTasks?: boolean
}

export const TaskStatusesBlocks: React.FC<TaskStatusesBlocksProps> = memo(
  ({ deepDiveInsights, isEmpty = false, isShowIcons = false, analytics, symbol, totalTasksCount, hasVMTasks }) => {
    const [isLoaded, setIsLoaded] = useState(false)

    const totalCount = useMemo(() => {
      if (analytics && totalTasksCount !== undefined) {
        return totalTasksCount
      }
      if (deepDiveInsights?.contexts) {
        return Object.keys(deepDiveInsights.contexts).length
      }
      return 0
    }, [deepDiveInsights, analytics, totalTasksCount])

    const tasksStatuses = useMemo(() => {
      if (analytics) {
        return {
          statuses: {
            todo:
              analytics.find((item) => item.name === 'analytical_todo' || item.name === 'task_status_todo')?.value || 0,
            inProgress:
              analytics.find((item) => item.name === 'analytical_inprogress' || item.name === 'task_status_inprogress')
                ?.value || 0,
            done:
              analytics.find((item) => item.name === 'analytical_done' || item.name === 'task_status_done')?.value || 0,
            approved: analytics.find((item) => item.name === 'task_status_approved')?.value,
            rejected: analytics.find(
              (item) => item.name === 'analytical_rejected' || item.name === 'task_status_rejected'
            )?.value,
          },
        }
      }
      return calculateTaskStats(deepDiveInsights?.contexts || {})
    }, [analytics, deepDiveInsights])

    const task_status_done = useLocaleText('task_status_done')
    const task_status_inprogress = useLocaleText('task_status_inprogress')
    const task_status_todo = useLocaleText('task_status_todo')
    const task_status_approved = useLocaleText('task_status_approved') // NEW
    const task_status_rejected = useLocaleText('task_status_rejected') // NEW
    const total_tasks_label = analytics ? useLocaleText('total_tasks') : useLocaleText('total_contexts')

    // Build status items array - always include all 5 statuses + 1 total block
    const statusItems = [
      {
        key: 'total',
        title: total_tasks_label,
        color: '#FFFFFF', // white background
        iconBgColor: '#94A3B8', // grey for icon background
        icon: <Hash size={18} color="white" />,
      },
      {
        key: 'todo',
        title: task_status_todo,
        color: CHART_COLORS.todo, // pastel grey
        iconBgColor: CHART_COLORS.todoText,
        icon: <AlarmClockCheck size={18} color="white" />,
      },
      {
        key: 'inProgress',
        title: task_status_inprogress,
        color: CHART_COLORS.inProgress, // pastel yellow
        iconBgColor: CHART_COLORS.inProgressText,
        icon: <ChartSpline size={18} color="white" />,
      },
      {
        key: 'done',
        title: task_status_done,
        color: CHART_COLORS.done, // pastel blue
        iconBgColor: CHART_COLORS.doneText,
        icon: <CircleCheckBig size={18} color="white" />,
      },
      ...(hasVMTasks
        ? [
            {
              key: 'approved',
              title: task_status_approved,
              color: CHART_COLORS.approved,
              iconBgColor: CHART_COLORS.approvedText,
              icon: <ThumbsUp size={18} color="white" />,
            },
          ]
        : []),
      ...(hasVMTasks
        ? [
            {
              key: 'rejected',
              title: task_status_rejected,
              color: CHART_COLORS.rejected,
              iconBgColor: CHART_COLORS.rejectedText,
              icon: <ThumbsDown size={18} color="white" />,
            },
          ]
        : []),
    ]
    useEffect(() => {
      if (!isEmpty) {
        const timer = setTimeout(() => setIsLoaded(true), 0)
        return () => clearTimeout(timer)
      } else {
        setIsLoaded(false)
      }
    }, [isEmpty])

    return (
      <Box w="100%" my="10px">
        <Grid
          w="100%"
          gap="15px"
          css={css`
            display: grid;
            grid-template-columns: repeat(${Math.ceil(statusItems.length / 2)}, 1fr);
            ${statusItems.length === 4 &&
            `
             @media (min-width: 1280px) {
                grid-template-columns: repeat(4, 1fr);
              }
            `}
          `}
        >
          {statusItems.map(({ key, title, color, iconBgColor, icon }) => {
            const count =
              key === 'total' ? totalCount : tasksStatuses?.statuses[key as keyof TaskStats['statuses']] || 0
            const isTotalBlock = key === 'total'
            return (
              <TaskStatusItem
                key={key}
                icon={icon}
                isShowIcons={isShowIcons && !isTotalBlock}
                title={title}
                count={count}
                bgColor={color}
                iconBgColor={iconBgColor}
                isEmpty={isEmpty}
                isLoaded={isLoaded}
                symbol={isTotalBlock ? '' : symbol ?? ''}
              />
            )
          })}
        </Grid>
      </Box>
    )
  }
)
